import { useContext, useEffect, useState } from 'react';
import { NextPage } from 'next';
import ProductHead from './components/ProductHead';

import Pds08Locale from 'locale/en/pds08.json';

import ModalPDS08Sustainability from 'components/modal/pds08-sustainability';
import ModalPDS08Usage from 'components/modal/pds08-usage';
import ModalPDS08Benefits from 'components/modal/pds08-benefits';
import ModalPDS08Facts from 'components/modal/pds08-facts';

import SectionHero from 'components/pds08/hero';
import SectionBenefits from 'components/pds08/benefits';
import SectionBridgeGap from 'components/pds08/bridge-gap';
import SectionUsage from 'components/pds08/usage';
import SectionSustainability from 'components/pds08/sustainability';
import SectionNewStandard from 'components/pds08/new-standard';
import SectionFormulation from 'components/pds08/formulation';
import SectionMeetScicare from 'components/pds08/meet-scicare';
import SectionCurious from 'components/pds08/curious';
import StickyBar from 'components/global/StickyBar';
import { usePdpStickyBar } from 'hooks/use-pdp-sticky-bar';
import {
  buildMetaUserData,
  propertiesForProduct,
  trackEvent,
} from 'lib/analytics';
import SitePopup from 'components/site/site-popup';
import { RootState } from 'entities/rootState';
import { ProductsContext } from 'context-providers/products-provider';
import { UserContext } from 'context-providers/user-provider';
import { createGlobalStyle } from 'styled-components';
import { YotpoReviewScript } from 'components/pdp/yotpo-reviews-script';
import { ReviewsDisplay } from 'components/pdp/reviews-display';
import { YotpoLlmSchema } from 'components/pdp/yotpo-llm-schema';
import { apiPlansGetV2, planPageType } from 'api/plans/get';
import { Product } from 'entities/product';
import { getYotpoBottomline } from 'lib/utils/get-yotpo-bottomline';
import { getYotpoLlmSchema } from 'lib/utils/get-yotpo-llm-schema';
import { PageTypePdp } from 'components/styles/pdp';
import { Pds08Styles } from 'components/styles/pds08';
import { LightningMode } from 'components/styles/lightning-mode';
import LazyLoad from 'components/reusable/Lazy/lazy_load';
import { buildPdsPriceString } from 'lib/utils/build-pds-price-string';
import { getLocalizedPrice } from 'lib/utils/get-localized-price';
import { pageReplaced } from 'lib/utils/page-replaced';
import { showPopup } from 'lib/utils/show-popup';
import { getContentfulDataDirect } from 'lib/contentful/get-contentful-data-direct';
import { getShopifyVariantResults } from 'lib/shopify/get-shopify-variants-direct';
import { getProductProps } from 'lib/utils/get-product-static-props';
import { attachPdpModulesToProps } from 'lib/pdp-modules/get-pdp-modules';
import { hasRichText } from 'lib/pdp-modules/helpers';
import { portableTextToShopifyRichText } from 'lib/pdp-modules/shopify-rich-text';
import { convertSchemaToHtml } from '@thebeyondgroup/shopify-rich-text-renderer';
import { buildFaqSchema } from 'lib/utils/build-faq-schema';
import { PRODUCT_IMAGE_PDS08 } from 'lib/product-data';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';

const handleUrlHashModalOpen = (lookupMap) => {
  const urlParts = location.href.split('#');
  const hashParameter = urlParts.length > 1 && urlParts.slice(-1)[0];
  if (hashParameter in lookupMap) {
    // popup can only open on the next event loop tick
    process.nextTick(() => {
      showPopup(lookupMap[hashParameter]);
    });
  }
};

const GlobalStylePds08DailyKidsProbiotic = createGlobalStyle`
  ${LightningMode({ includeLayout: true, includePdpModule: true, includeForms: true })}

  ${Pds08Styles}
  ${PageTypePdp}
`;

const PageData = {
  page_type: 'default',
  page_class: 'page-pds08 scrolled-long-mobile',
  page_name: Pds08Locale.page_name,
  page_title: Pds08Locale.page_title,
  page_description: Pds08Locale.page_desc,
};

type PDS08Props = {
  cartCode: string;
  curiousQuestions: any[];
  siteBuy: any;
  productInfo?: any;
  faqSchema?: object | null;
  yotpoAverageScore?: string | number | null;
  yotpoTotalReviews?: string | number | null;
  yotpoLlmSchemaHtml?: string;
} & RootState;

const PDS08: NextPage<PDS08Props> = (props: PDS08Props) => {
  const { getProduct, currencyCode } = useContext(ProductsContext);
  const { userState } = useContext(UserContext);
  const product = getProduct(PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC);
  const priceString = buildPdsPriceString(
    getLocalizedPrice(product),
    currencyCode,
  );

  const [isClientLoaded, setClientLoaded] = useState(false);

  useEffect(() => {
    pageReplaced(PageData, false);
    (async () => {
      const userData = await buildMetaUserData(userState);
      trackEvent('Product Viewed', {
        ...propertiesForProduct(product),
        ...(userData ? { userData } : {}),
      });
    })();
    setClientLoaded(true);
  }, []);

  useEffect(() => {
    // handle "hash" tags in the URL
    handleUrlHashModalOpen({
      supplement_facts: 'modal-pds08-facts',
    });
  }, []);

  const {
    curiousQuestions,
    siteBuy,
    productInfo,
    faqSchema,
    yotpoAverageScore,
    yotpoTotalReviews,
    yotpoLlmSchemaHtml,
  } = props;
  const stickyBarProps = usePdpStickyBar(
    PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC,
    siteBuy,
  );

  const title = `Seed • PDS-08® Pediatric Daily Synbiotic | Probiotic for Kids`;
  return (
    <>
      <GlobalStylePds08DailyKidsProbiotic />
      <ProductHead
        productName='PDS-08™ Pediatric Daily Synbiotic (Probiotic + Prebiotic)'
        productImages={[
          'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649799522/pediatric-daily-synbiotic/PDS_Bactrac_and_sachet.png',
          'https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1649193400/pediatric-daily-synbiotic/pds_full_width_wk.png',
        ]}
        productDescription={`Introducing PDS-08™: a daily, 2-in-1 probiotic + prebiotic formulated for kids, with 5 grams of prebiotic fiber and 9 clinically studied probiotic strains. PDS-08™ is scientifically engineered to survive; clinically studied to support easy, frequent poops; and formulated to deliver benefits in and beyond the gut, with evidence-based strains to support your child's gastrointestinal health, gut barrier integrity, gut immune function, dermatological health, and respiratory health. Your first month includes a free (reusable, refillable) BacTrac™ container for built-in daily tracking, designed with busy parents—and Earth—in mind. Gluten-free. Dairy-free. Sugar-free. Free of 14 classes of allergens. For ages 3–17.`}
        productSku='PDS-WK'
        canonicalUrl='https://seed.com/pds08-daily-kids-probiotic'
        productPrice={productInfo?.priceRange?.minVariantPrice?.amount}
        pageTitle={title}
        ogImageUrl='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1759517361/structured-data/og-pds.png'
        ogImageAlt='PDS-08 Pediatric Daily Synbiotic (Probiotic + Prebiotic)'
        enableRecaptcha={true}
        faqSchema={faqSchema}
        yotpoAverageScore={yotpoAverageScore}
        yotpoTotalReviews={yotpoTotalReviews}
      />
      <YotpoReviewScript />

      <div className='container'>
        <div className='body'>
          <div className='card'>
            <SectionHero
              product={product}
              siteBuy={siteBuy}
              priceString={priceString}
            />
            <SectionUsage />
            {isClientLoaded ? (
              <>
                <LazyLoad height='200px'>
                  <SectionBenefits />
                </LazyLoad>
                <LazyLoad height='200px'>
                  <SectionFormulation />
                </LazyLoad>
                <LazyLoad height='200px'>
                  <SectionBridgeGap />
                </LazyLoad>
                <LazyLoad height='200px'>
                  <SectionSustainability priceString={priceString} />
                </LazyLoad>
                <LazyLoad height='200px'>
                  <SectionNewStandard />
                </LazyLoad>
                <LazyLoad height='200px'>
                  <SectionMeetScicare />
                </LazyLoad>
                <LazyLoad height='200px'>
                  <SectionCurious questions={curiousQuestions} />
                </LazyLoad>
              </>
            ) : null}
          </div>
          {product ? (
            <ReviewsDisplay
              imageUrl='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1722005136/Channel%20Catalogs/1_1_PDS-08_2x.jpg'
              product={product}
              url='/pds08-daily-kids-probiotic'
            />
          ) : null}
          <YotpoLlmSchema html={yotpoLlmSchemaHtml} />
        </div>

        {isClientLoaded ? (
          <>
            <StickyBar
              {...stickyBarProps}
              imageSrc={PRODUCT_IMAGE_PDS08}
              buttonEventName='PDS start now'
            />

            <SitePopup id='modal-pds08-benefits' type='drawer'>
              <ModalPDS08Benefits />
            </SitePopup>

            <SitePopup id='modal-pds08-usage' type='drawer'>
              <ModalPDS08Usage />
            </SitePopup>

            <SitePopup id='modal-pds08-sustainability' type='drawer'>
              <ModalPDS08Sustainability />
            </SitePopup>

            <SitePopup id='modal-pds08-facts' type='drawer'>
              <ModalPDS08Facts />
            </SitePopup>
          </>
        ) : null}
      </div>
    </>
  );
};

type PDS08StaticProps = Partial<PDS08Props> & {
  products: Product[];
  shopifyProducts?: any;
  shopifyFAQs?: any;
};

export async function getStaticProps() {
  const products = await apiPlansGetV2(planPageType.all);

  const [{ yotpoAverageScore, yotpoTotalReviews }, yotpoLlmSchemaHtml] =
    await Promise.all([
      getYotpoBottomline(PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC),
      getYotpoLlmSchema(PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC),
    ]);

  const props: PDS08StaticProps = {
    products,
    yotpoAverageScore,
    yotpoTotalReviews,
    yotpoLlmSchemaHtml,
  };

  const labelSiteBuyQuery = {
    content_type: 'siteBuy',
    'fields.title': 'pds-08',
  };
  props.siteBuy = (await getContentfulDataDirect(labelSiteBuyQuery)).items[0];

  const { results } = await getShopifyVariantResults({
    requiredSkus: [PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC],
  });
  props.shopifyProducts = results;

  await getProductProps({
    props: props,
    productSku: PRODUCT_SKUS.PDS08_DAILY_KIDS_PROBIOTIC,
    shopifyVariantData: props.shopifyProducts,
  });

  const modules = await attachPdpModulesToProps('pds-08', props);

  // SiteAccordion renders Shopify rich-text JSON, so answers convert back from Portable Text.
  if (modules.faqModule) {
    props.curiousQuestions = modules.faqModule.items.map((faq) => {
      return {
        fields: {
          question: faq.question ?? null,
          response: hasRichText(faq.answer)
            ? JSON.parse(portableTextToShopifyRichText(faq.answer))
            : null,
        },
      };
    });
  }

  if (props.curiousQuestions?.length) {
    const faqItems = props.curiousQuestions
      .map((item) => ({
        question: item.fields.question as string,
        answer: item.fields.response
          ? convertSchemaToHtml(item.fields.response) || ''
          : '',
      }))
      .filter((item) => item.question);
    props.faqSchema = buildFaqSchema(faqItems);
  }

  return {
    props,
    revalidate: 60,
  };
}

export default PDS08;
